/**
 * LocalLoginPage — the email + password sign-in: AMD's Okta layout, this app's
 * theme, the legacy app's API.
 *
 * One of the TWO sign-in screens (see config/loginMode.js). It posts to this
 * app's own /login API, whereas the Advisory screen (HomePage) hands off to
 * Common Services, whose session cookies never reach localhost.
 *
 * Two ways to reach it:
 *  - the landing URL, on localhost, where it is the default screen
 *    (RequireAuth shows it to anyone signed out);
 *  - /login, on EVERY host (user order 2026-10-23, as legacy's /login route).
 *
 * On success it navigates NOWHERE itself — signing in flips the guard, which
 * then shows the page the visitor originally asked for (remembered in history
 * state, never in the address bar), or the product's default section.
 *
 * Three sources, on purpose:
 *  - LAYOUT from AMD's Okta-hosted sign-in page: carbon backdrop, one centred
 *    panel, product lockup, "Sign-In", two fields, one full-width button. The
 *    OR divider, Create Account, Forgot / Reset Password and the
 *    Help / Terms / Privacy row are intentionally not here. The shared chrome
 *    (backdrop, panel, lockup, product line, legal notice) lives in LoginShell.
 *  - LOCKUP per variant: the CCA badge for CCA, EIA's for EIA, instead of the
 *    plain AMD wordmark Okta shows — the shell already knows which product it
 *    is serving, so it may as well say so.
 *  - BEHAVIOUR from the legacy Vue sign-in (Login.vue): the same email regex
 *    and 6-character password minimum, and the same POST /login ->
 *    POST /getUserToken sequence (see useLogin.js).
 *
 * Kept from legacy:
 *  - the email regex and password rule (Login.vue:112-124);
 *  - Sign In disabled until both fields have content (Login.vue:203-205).
 *
 * Deliberately not kept:
 *  - Validation messages actually render. Legacy ships
 *    `.v-input__details { display: none }` in index.html, so its rules run and
 *    are never shown to anyone.
 *  - One error region instead of two snackbars, and no success toast — on
 *    success the app appears in place of the form, which is its own feedback.
 *
 * Default-exported for lazy loading.
 */
import { useState } from 'react';

import { TextField } from '@components/common/TextField';
import { Alert, Button } from '@components/ui';

import { LoginShell } from '../components/LoginShell';
import { useLogin } from '../useLogin';

import styles from './LocalLoginPage.module.css';

/** Legacy's rule, Login.vue:113. Kept identical so the same inputs pass. */
const EMAIL_PATTERN = /^[\w-]+@([\w-]+\.)+[\w-]{2,4}$/;
const MIN_PASSWORD_LENGTH = 6;

function validate({ email, password }) {
  const errors = {};
  if (!email) errors.email = 'Email is required';
  else if (!EMAIL_PATTERN.test(email)) errors.email = 'Invalid Email Address';

  if (!password) errors.password = 'Password is required';
  else if (password.length < MIN_PASSWORD_LENGTH) {
    errors.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters long`;
  }
  return errors;
}

export default function LocalLoginPage() {
  const { submit, error, clearError, isSubmitting } = useLogin();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Rules only render after a submit attempt, so the form does not shout at
  // someone who has merely tabbed through it.
  const [touched, setTouched] = useState(false);

  const errors = validate({ email, password });
  const shown = touched ? errors : {};
  // Legacy disables on emptiness only (isButtonDisabled), not on validity, so a
  // malformed email still submits and the rules explain why. Same here.
  const canSubmit = Boolean(email && password) && !isSubmitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (Object.keys(errors).length > 0) return;

    // No navigation on success: `submit` marks the session authenticated, and
    // RequireAuth then shows the requested page instead of this form.
    await submit({ email, password });
  };

  return (
    <LoginShell>
      <form onSubmit={handleSubmit} className={styles.form} noValidate>
        {error ? (
          // "danger" — the library Alert has no "error" variant; an unknown one
          // renders with no colour and no icon.
          <Alert variant="danger" role="alert" onClose={clearError} className={styles.error}>
            {error}
          </Alert>
        ) : null}

        <TextField
          id="email-field"
          label="E-mail Address"
          required
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={shown.email}
          disabled={isSubmitting}
        />

        <TextField
          id="password-field"
          label="Password"
          required
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={shown.password}
          disabled={isSubmitting}
        />

        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={!canSubmit} loading={isSubmitting}>
            Sign in
          </Button>
        </div>
      </form>
    </LoginShell>
  );
}
